import { defineStore } from 'pinia';
import type { TFacilityTree } from '../types/facility';
import type { TStoreUser } from '../types/user';
import { ERoleIdentifier, EUserFacilityRelationType } from '../types/user';

type TMainStore = {
  user: TStoreUser
  facilityTree: TFacilityTree | null
  specialities: TSpecialities
};

export const useMainStore = defineStore('main', {
  state: (): TMainStore => ({
    user: {},
    facilityTree: null,
    specialities: [],
  }),
  getters: {
    hasRole({ user }): (roles: ReadonlyArray<ERoleIdentifier>) => boolean {
      return (roles) => {
        const identifier = user.role?.identifier || false;
        return Boolean(identifier && roles.length && roles.includes(identifier));
      };
    },
    isNonAdminFacilityPlanner: ({ user }) =>
      user.role?.identifier === ERoleIdentifier.ADMINISTRATIVE_STAFF
      && !user.main_facility_admin
      && (user.facilities?.some(
        (f) =>
          f.type === EUserFacilityRelationType.ADMINISTRATE
          || f.type === EUserFacilityRelationType.COORDINATE,
      )
        ?? false),
    isReadOnly: ({ user }) => user.role?.identifier === ERoleIdentifier.READ_ONLY,
    hasNetworkCompetencesAccess: ({ user }) =>
      user.role?.identifier === ERoleIdentifier.ADMINISTRATIVE_STAFF
      && !!user.main_facility_admin
      && !!user.main_facility?.premium_features?.includes('NETWORK_COMPETENCES'),
    isClinicStructureReadOnly(): boolean {
      return this.isReadOnly || this.isNonAdminFacilityPlanner;
    },
    hasAdministrativeAccess(): boolean {
      return this.hasRole([ERoleIdentifier.ADMINISTRATIVE_STAFF, ERoleIdentifier.READ_ONLY]);
    },
    isUnrestrictedPlanningGroupAccess: ({ user }) => !!user.main_facility_admin,
    permittedDefaultPlanningGroupIds: ({ user }) => new Set(
      (user.planning_groups ?? [])
        .filter((pg) => pg.type === 'default' && pg.planning_group_id)
        .map((pg) => pg.planning_group_id),
    ),
    permittedPlanningGroupIds: ({ user }) => (user.planning_groups ?? [])
      .filter((pg) => pg.planning_group_id)
      .map((pg) => pg.planning_group_id),
    filteredFavoritePlanningGroups: ({ user }) => {
      const favorites = user.favorite_planning_groups ?? [];
      if (user.main_facility_admin) return favorites;
      const ids = (user.planning_groups ?? [])
        .filter((pg) => pg.planning_group_id)
        .map((pg) => pg.planning_group_id);
      return favorites.filter((f) => ids.includes(f.planning_group_id.id));
    },
    permittedUserFacilityIds: ({ user }) => groupFacilityIdsByType(user.facilities),
  },
  actions: {
    setUserContact(user: TStoreUser) {
      this.user.gender = user.gender;
      this.user.title = user.title;
      this.user.first_name = user.first_name;
      this.user.last_name = user.last_name;
      this.user.email = user.email;
    },
    setTfa(tfaSecret: string) {
      this.user.tfa_secret = tfaSecret;
    },
  },
});
